<template>
  <main class="event-code">
    <h1>Código do Evento</h1>
    <p v-if="session">Este código está reservado para o seu atendimento.</p>
    <p v-else>Digite o código recebido do organizador para começar.</p>
    <form v-if="!session" @submit.prevent="autorizar">
      <input
        v-model="code"
        maxlength="14"
        autocomplete="off"
        autocapitalize="characters"
        aria-label="Código do Evento"
        :disabled="busy || !!requestId"
        @input="normalizar"
      />
      <div v-if="!requestId" class="event-code__keyboard">
        <button
          v-for="key in alphabet"
          :key="key"
          type="button"
          :disabled="busy"
          @click="code = (code + key).slice(0, 12)"
        >
          {{ key }}</button
        ><button type="button" @click="code = code.slice(0, -1)">Apagar</button>
      </div>
      <button :disabled="busy || code.length !== 12">
        {{ busy ? "Verificando…" : "Continuar" }}
      </button>
    </form>
    <button v-else type="button" :disabled="busy" @click="retomar">
      Retomar atendimento
    </button>
    <p v-if="error" role="alert">{{ error }}</p>
    <button type="button" :disabled="busy" @click="voltar">Voltar</button>
  </main>
</template>
<script>
import { db, iniciarCompra } from "@/service/firebase";
import { doc, getDoc } from "@firebase/firestore";
import { COLLECTION_COMPRAS } from "@/service/collectionsNames";
import {
  currentAttendance,
  saveAttendance,
  clearAttendance,
  pendingEventCode,
  eventRequestId,
  reserveEventCode,
  releaseAttendance,
  eventAttendanceStatus,
  assertEventSession,
} from "@/service/eventAttendance";
import { pararEscutaCompraAtual } from "@/service/purchaseSession";
export default {
  data: () => ({
    code: "",
    alphabet: "ABCDEFGHJKLMNPQRSTUVWXYZ23456789".split(""),
    session: null,
    busy: false,
    error: "",
    requestId: null,
    requestCode: "",
    destroyed: false,
  }),
  computed: {
    totemId() {
      return this.$store.getters["totem/getTotem"]?.id;
    },
  },
  mounted() {
    pararEscutaCompraAtual();
    try {
      this.session = currentAttendance(this.totemId);
      const pending = pendingEventCode(this.totemId);
      if (!this.session && pending) {
        this.code = pending.code;
        this.requestId = pending.requestId;
        this.requestCode = pending.code;
      }
    } catch (e) {
      this.error = e.message;
    }
  },
  beforeDestroy() {
    this.destroyed = true;
  },
  methods: {
    normalizar() {
      this.code = this.code
        .toUpperCase()
        .replace(/[^A-HJ-NP-Z2-9]/g, "")
        .slice(0, 12);
    },
    mensagem(e) {
      return e?.response?.status === 409
        ? "Este código já está em uso ou foi utilizado. Peça ajuda ao organizador."
        : "Não foi possível verificar o atendimento. Confira a conexão e tente novamente.";
    },
    async autorizar() {
      if (this.busy || this.code.length !== 12) return;
      this.busy = true;
      this.error = "";
      try {
        if (!this.requestId || this.requestCode !== this.code) {
          this.requestId = eventRequestId();
          this.requestCode = this.code;
        }
        this.session = await reserveEventCode(
          this.totemId,
          this.code,
          this.requestId
        );
        if (!this.destroyed) await this.iniciarOuRetomar();
      } catch (e) {
        if (!this.destroyed) {
          this.error = this.mensagem(e);
          if (!pendingEventCode(this.totemId)) this.requestId = null;
        }
      } finally {
        if (!this.destroyed) this.busy = false;
      }
    },
    async retomar() {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        await this.iniciarOuRetomar();
      } catch (e) {
        if (!this.destroyed) this.error = this.mensagem(e);
      } finally {
        if (!this.destroyed) this.busy = false;
      }
    },
    async iniciarOuRetomar() {
      const id = this.totemId;
      const session = this.session;
      assertEventSession(id, session);
      const state = await eventAttendanceStatus(id, session);
      if (this.destroyed) return;
      assertEventSession(this.totemId, session);
      if (state.status === "COMPLETED" && state.codeStatus === "CONSUMED") {
        clearAttendance(this.totemId);
        this.$store.commit("compra/resetCompra");
        this.session = null;
        this.code = "";
        this.requestId = null;
        this.error = "Atendimento concluído. Use um novo código.";
        return;
      }
      if (state.status === "RELEASED") {
        clearAttendance(this.totemId);
        this.session = null;
        return;
      }
      if (session.plan || (state.jobs || []).length) {
        const snap = await getDoc(
          doc(db, COLLECTION_COMPRAS, session.purchaseId)
        );
        if (this.destroyed) return;
        assertEventSession(this.totemId, session);
        if (!snap.exists() || snap.data().totem_id !== id)
          throw new Error("Compra indisponível.");
        this.$store.commit("compra/setCompra", {
          ...snap.data(),
          id: session.purchaseId,
        });
        await this.$router.replace("/cabine/impressao");
        return;
      }
      // Fix and persist the purchase ID before the first remote write.
      const purchaseId = session.purchaseId || doc(db, COLLECTION_COMPRAS).id;
      this.session = saveAttendance(id, { ...session, purchaseId });
      await new Promise((resolve, reject) => {
        iniciarCompra(
          () => {
            if (this.destroyed) {
              resolve();
              return;
            }
            try {
              assertEventSession(this.totemId, this.session);
              this.$root.CreatListenerPurchase();
              resolve();
            } catch (e) {
              reject(e);
            }
          },
          () => reject(new Error("Falha ao iniciar atendimento.")),
          { purchaseId, totemId: id }
        );
      });
    },
    async voltar() {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        if (this.session) await releaseAttendance(this.totemId);
        this.$store.commit("compra/resetCompra");
        await this.$router.replace("/cabine/home");
      } catch (e) {
        this.error =
          "Há uma impressão pendente. Retome o atendimento ou peça ajuda ao organizador.";
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
<style scoped>
.event-code {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  padding: 40px;
  background: #fff;
  color: #222;
  font-size: 28px;
  text-align: center;
}
.event-code h1 {
  font-size: 48px;
  font-weight: bold;
}
.event-code form {
  display: flex;
  flex-direction: column;
  gap: 30px;
}
.event-code input {
  border: 2px solid #ddd;
  padding: 20px;
  border-radius: 12px;
  letter-spacing: 8px;
  text-align: center;
  font-family: monospace;
}
.event-code button {
  background: #ed4085;
  color: white;
  border-radius: 12px;
  padding: 20px 40px;
}
.event-code button:disabled {
  opacity: 0.5;
}
.event-code__keyboard {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 10px;
}
.event-code__keyboard button {
  padding: 14px;
}
</style>
